import { Link, useLocation } from "react-router-dom";
import { PRODUCT_NAME, COMPANY_LINE, RIGHTS_LINE, LOGO_SRC } from "@/lib/brand";
import { ArrowLeft } from "lucide-react";

// Lightweight, functional Privacy Policy / Terms of Service pages (public).
export default function LegalPage() {
  const { pathname } = useLocation();
  const isPrivacy = pathname.includes("privacy");
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#08122e] text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="bg-white rounded-lg p-1.5">
            <img src={LOGO_SRC} alt="REASON" className="h-8 w-8 object-contain" />
          </div>
          <span className="font-extrabold text-lg">{PRODUCT_NAME}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#118D89] hover:underline mb-6">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <h1 className="text-3xl font-extrabold text-slate-900">{title}</h1>
        <p className="text-slate-500 text-sm mt-1">Last updated: 2024</p>

        <div className="prose prose-slate max-w-none text-sm text-slate-700 mt-6 space-y-4">
          {isPrivacy ? (
            <>
              <p>This Privacy Policy explains how Reason Analytics Softech Pvt Ltd ("we", "us") collects,
                uses, and protects information processed through {PRODUCT_NAME}.</p>
              <h2 className="text-lg font-bold text-slate-900">Data we process</h2>
              <p>We process account, tenant, and customer-relationship data you provide to operate the
                platform. Data is logically isolated per tenant and protected with role-based access
                control, encryption of sensitive fields, and an immutable audit trail.</p>
              <h2 className="text-lg font-bold text-slate-900">Your rights</h2>
              <p>Tenants can exercise data-subject rights (access, export, erasure) through the platform's
                compliance tools. Contact your administrator or our Data Protection Officer for requests.</p>
              <h2 className="text-lg font-bold text-slate-900">Security</h2>
              <p>We apply enterprise-grade safeguards including JWT authentication, MFA, rate limiting,
                security headers, and continuous monitoring.</p>
            </>
          ) : (
            <>
              <p>These Terms of Service govern your use of {PRODUCT_NAME}, provided by Reason Analytics
                Softech Pvt Ltd. By accessing the platform you agree to these terms.</p>
              <h2 className="text-lg font-bold text-slate-900">Acceptable use</h2>
              <p>You agree to use the platform lawfully, to protect your credentials, and not to attempt
                to access data outside your authorized tenant or role.</p>
              <h2 className="text-lg font-bold text-slate-900">Subscriptions</h2>
              <p>Access is governed by your subscription plan and entitlements. Usage limits apply per
                plan and are enforced by the platform.</p>
              <h2 className="text-lg font-bold text-slate-900">Liability</h2>
              <p>The platform is provided on an enterprise SLA basis. Specific service levels are defined
                in your commercial agreement.</p>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 mt-10 pt-6 text-xs text-slate-400">
          {COMPANY_LINE} {RIGHTS_LINE}
        </div>
      </main>
    </div>
  );
}
